import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";
import { useNasalTiers, isNasalMist, tierUnitPrice, TierBreakdown } from "./NasalTiers";
import { SaleSummary } from "./SaleSummary";
import { PriceChips } from "./PriceChips";

// Special "seller" value indicating the owner is selling their own stock
// (used in Mark Sold mode only). When picked, we POST to /products/mark-sold
// with seller_type='owner' instead of /products/assign.
const SELLER_ME = "__me__";

export default function AssignProductDialog({
  product,
  onClose,
  onAssigned,
  defaultDistributorId,
  lockDistributor = false,
  // "Pass down" mode: move HELD stock from this holder → a distributor
  // (consignment only; the holder is excluded from the target list).
  holderUserId = null,
  holderName = "",
  // v401.0: open directly in a specific mode (e.g. "sale" for the Master
  // Inventory "Mark Sold" button).
  initialTransferType = "consignment",
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [sellerChoice, setSellerChoice] = useState(SELLER_ME); // Mark Sold: "__me__" or distributor.id
  // v400.43 chained transfer: optionally pass the SAME quantity straight
  // down to a final recipient in the same atomic operation.
  // v402.9: hidden behind an explicit toggle — an always-visible second
  // dropdown invited accidental chain routes.
  const [showChain, setShowChain] = useState(false);
  const [passTargetId, setPassTargetId] = useState("");
  const [passMarkup, setPassMarkup] = useState("");
  // v400.54 quick markup — decided at TRANSFER time for held-stock hops.
  const [quickMarkup, setQuickMarkup] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [transferType, setTransferType] = useState(initialTransferType);
  // Sample flow payment status: "free" (default) | "paid" (paid up-front) |
  // "owed" (v407.0 — stock deducts now, amount tracked as OWED until the
  // matching e-transfer is logged as a Personal use payment).
  const [samplePayment, setSamplePayment] = useState("free");
  const samplePaid = samplePayment === "paid";
  const sampleOwed = samplePayment === "owed";
  const [samplePrice, setSamplePrice] = useState(
    product?.wholesale_price != null ? String(product.wholesale_price) : ""
  );
  const [salePrice, setSalePrice] = useState(
    product?.suggested_retail_price != null ? String(product.suggested_retail_price) : ""
  );
  const [busy, setBusy] = useState(false);
  // v44: Precision Nasal Mist sales use the tiered SRP table.
  const tiers = useNasalTiers();
  const mist = isNasalMist(product);
  const [priceDirty, setPriceDirty] = useState(false);
  // v400.61: partners/distributors give from their OWN consignment balance —
  // the master (org-root) source only remains for owner/admin and self-consign.
  const isNonOwnerGiver = user?.role === "partner" || user?.role === "distributor";
  const [ownHeldRow, setOwnHeldRow] = useState(null);

  useEffect(() => {
    if (!isNonOwnerGiver || holderUserId) return;
    api
      .get(`/distributors/${user.id}/inventory`)
      .then(({ data }) => {
        const row = (data || []).find(
          (r) => r.id === product.id || r.source_product_id === product.id
        );
        setOwnHeldRow(row || null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, holderUserId]);

  useEffect(() => {
    api
      .get("/distributors")
      .then(({ data }) => {
        // v409.2 — central inventory: a personal entry is only a valid
        // recipient when the SOURCE is its parent's own stock (the giver
        // themselves, or the held-stock holder in pass-down mode).
        const list = (holderUserId ? data.filter((d) => d.id !== holderUserId) : data)
          .filter((d) => !d.is_personal || d.owner_id === (holderUserId || user?.id));
        setDistributors(list);
        if (defaultDistributorId && list.some((d) => d.id === defaultDistributorId)) {
          setDistributorId(defaultDistributorId);
          // If the caller pinned a distributor, default the sale seller to them
          // (matches the previous behavior of "give more to THIS distributor").
          setSellerChoice(defaultDistributorId);
        } else if (list.length === 1) {
          // v402.9: auto-select ONLY when unambiguous. Silently defaulting to
          // the first distributor caused accidental "via Harvey" chain routes
          // when the final recipient was picked in the pass-down box instead.
          setDistributorId(list[0].id);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [defaultDistributorId, holderUserId]);

  // v414.1 — US/Mexico recipients: prefill sale/sample prices from the org
  // currency sheet (seller-protective USD/MXN numbers, editable as always).
  const [sheetInfo, setSheetInfo] = useState(null);
  useEffect(() => {
    if (!distributorId || distributorId === user?.id) { setSheetInfo(null); return; }
    api.get(`/price-sheets/for/${distributorId}`)
      .then(({ data }) => {
        if (!data || data.currency === "CAD") { setSheetInfo(null); return; }
        const masterId = product.source_product_id || product.id;
        const row = (data.prices || {})[masterId] || {};
        setSheetInfo({ currency: data.currency, ws: row.ws ?? null, retail: row.retail ?? null });
        if (row.retail != null && !priceDirty) setSalePrice(String(row.retail));
        if (row.ws != null) setSamplePrice(String(row.ws));
      })
      .catch(() => setSheetInfo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId]);

  const isSample = transferType === "sample";
  const isSale = transferType === "sale";
  const isConsign = transferType === "consignment";
  const hasDistributors = distributors.length > 0;
  // No-distributors path → only "Me" can sell, no consignment.
  const showOnlySampleOption = !hasDistributors && !isSale;
  // v400.61: implicit held-source for partners/distributors giving to someone
  // else — the transfer draws from THEIR consignment balance, not master.
  const selfConsign = !!user && distributorId === user.id;
  const useOwnHeld = isNonOwnerGiver && !holderUserId && isConsign && !selfConsign;
  const sourceRow = holderUserId ? product : useOwnHeld ? ownHeldRow : product;
  const sourceStock = sourceRow?.current_stock ?? 0;
  // Held-stock hop: pass-down mode OR a non-owner giving their own stock.
  // The giver decides the recipient's wholesale price right here.
  const heldSource = !!holderUserId || user?.role === "distributor" || useOwnHeld;

  // Prefill the quick markup with the recipient's default margin (if any)
  // whenever the recipient changes.
  useEffect(() => {
    if (!heldSource) return;
    const rec = distributors.find((d) => d.id === distributorId);
    const flat = rec && (rec.margin_type || "flat") === "flat";
    setQuickMarkup(flat && Number(rec.margin_surcharge) > 0 ? String(rec.margin_surcharge) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, distributors]);

  useEffect(() => {
    if (!passTargetId) return;
    const rec = distributors.find((d) => d.id === passTargetId);
    const flat = rec && (rec.margin_type || "flat") === "flat";
    setPassMarkup(flat && Number(rec.margin_surcharge) > 0 ? String(rec.margin_surcharge) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passTargetId]);

  // Never allow the chain target to equal the intermediary.
  useEffect(() => {
    if (passTargetId && passTargetId === distributorId) setPassTargetId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId]);

  // "Raju Virk · RV-01 · under Harvey" — code + tree position prevent
  // mis-assignments as the hierarchy grows. Parent shown only below the root.
  const optionLabel = (d) => {
    const bits = [d.name];
    if (d.distributor_code) bits.push(d.distributor_code);
    if ((d.depth ?? 1) > 1 && d.parent_name) bits.push(t("assign.under", { name: d.parent_name }));
    else if (d.role && d.role !== "distributor") bits.push(t("distributors.partner_badge"));
    return bits.join(" · ");
  };

  const submit = async (e) => {
    e.preventDefault();
    const qty = Math.max(1, Number(quantity) || 0);
    if (qty > sourceStock) {
      toast.error(t("assign.only_n_on_hand", { n: sourceStock }));
      return;
    }
    const trimmedNote = note.trim().slice(0, 500);

    // ---------- Mark Sample ----------
    if (isSample) {
      const name = recipientName.trim();
      if (!name) {
        toast.error(t("assign.recipient_required"));
        return;
      }
      setBusy(true);
      try {
        const samplePriceNum = samplePrice === "" ? null : Number(samplePrice);
        if ((samplePaid || sampleOwed) && samplePriceNum !== null && (!Number.isFinite(samplePriceNum) || samplePriceNum < 0)) {
          toast.error(t("assign.invalid_sale_price"));
          setBusy(false);
          return;
        }
        await api.post("/products/mark-sample", {
          product_id: product.id,
          quantity: qty,
          recipient_name: name,
          note: trimmedNote || null,
          paid: samplePaid,
          owed: sampleOwed,
          sale_price: samplePaid || sampleOwed ? (samplePriceNum != null ? samplePriceNum : undefined) : undefined,
        });
        toast.success(
          samplePaid
            ? t("assign.sampled_paid_n", { n: qty, name: product.name })
            : sampleOwed
              ? t("assign.sampled_owed_n", { n: qty, name: product.name })
              : t("assign.sampled_n", { n: qty, name: product.name })
        );
        onAssigned();
        onClose();
      } catch (e2) {
        toast.error(formatApiError(e2));
      } finally {
        setBusy(false);
      }
      return;
    }

    // ---------- Mark Sold ----------
    if (isSale) {
      const sp = salePrice === "" ? null : Number(salePrice);
      if (sp !== null && (!Number.isFinite(sp) || sp < 0)) {
        toast.error(t("assign.invalid_sale_price"));
        return;
      }
      // A sale with no price and no SRP fallback would silently log
      // sale_price null → Movement Log / revenue fall back to wholesale
      // (production bug: $25 sale displayed as WS $55). Require the price.
      if (sp === null && product?.suggested_retail_price == null) {
        toast.error(t("assign.sale_price_required"));
        return;
      }
      setBusy(true);
      try {
        if (sellerChoice === SELLER_ME) {
          // Owner is the seller — credit revenue to owner, no distributor needed.
          await api.post("/products/mark-sold", {
            product_id: product.id,
            quantity: qty,
            seller_type: "owner",
            sale_price: sp == null ? undefined : sp,
            recipient_name: recipientName.trim() || null,
            note: trimmedNote || null,
          });
        } else {
          // Distributor is the seller — assign + sale to that distributor.
          if (!sellerChoice) {
            toast.error(t("assign.pick_distributor"));
            setBusy(false);
            return;
          }
          await api.post("/products/assign", {
            distributor_id: sellerChoice,
            product_id: product.id,
            quantity: qty,
            transfer_type: "sale",
            sale_price: sp == null ? undefined : sp,
            note: trimmedNote || null,
          });
        }
        toast.success(t("assign.sold_n", { n: qty, name: product.name }));
        onAssigned();
        onClose();
      } catch (e2) {
        toast.error(formatApiError(e2));
      } finally {
        setBusy(false);
      }
      return;
    }

    // ---------- Consignment ----------
    if (!distributorId) {
      toast.error(t("assign.pick_distributor"));
      return;
    }
    setBusy(true);
    try {
      const chained = passTargetId && !holderUserId;
      await api.post("/products/assign", {
        distributor_id: distributorId,
        product_id: useOwnHeld && ownHeldRow ? ownHeldRow.id : product.id,
        quantity: qty,
        transfer_type: "consignment",
        holder_user_id: holderUserId || (useOwnHeld ? user.id : undefined),
        markup_per_unit: heldSource && Number(quickMarkup) > 0 ? Number(quickMarkup) : undefined,
        chain_to_distributor_id: chained ? passTargetId : undefined,
        chain_markup_per_unit: chained && Number(passMarkup) > 0 ? Number(passMarkup) : undefined,
        note: trimmedNote || null,
      });
      if (chained) {
        const midName = distributors.find((d) => d.id === distributorId)?.name || "";
        const endName = distributors.find((d) => d.id === passTargetId)?.name || "";
        toast.success(t("assign.chain_success", { n: qty, mid: midName, name: endName }));
      } else {
        toast.success(t("assign.given_n", { n: qty, name: product.name }));
      }
      onAssigned();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="assign-dialog"
      >
        <h2 className="text-2xl font-semibold mb-1">{t("assign.dialog_title")}</h2>
        <p className="text-sm text-zinc-600 mb-4">
          {holderUserId
            ? t("assign.dialog_subtitle_passdown", { name: product.name, n: sourceStock, holder: holderName })
            : isSample
              ? t("assign.dialog_subtitle_sample", { name: product.name, n: sourceStock })
              : t("assign.dialog_subtitle", { name: product.name, n: sourceStock })}
        </p>

        <form onSubmit={submit} className="space-y-3">
          {/* Action type toggle — hidden in Pass-down mode (consignment only) */}
          {!holderUserId && (
          <div>
            <label className="block text-xs font-semibold mb-1.5">{t("assign.transfer_type")}</label>
            <div className="grid grid-cols-3 gap-2" data-testid="transfer-type-toggle">
              <button
                type="button"
                onClick={() => setTransferType("consignment")}
                disabled={!hasDistributors}
                data-testid="transfer-type-consignment"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isConsign
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.consign")}
              </button>
              <button
                type="button"
                onClick={() => setTransferType("sale")}
                data-testid="transfer-type-sale"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  isSale
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.mark_sold")}
              </button>
              <button
                type="button"
                onClick={() => setTransferType("sample")}
                data-testid="transfer-type-sample"
                className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                  isSample
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                {t("assign.mark_sample")}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1.5">
              {isConsign ? t("assign.consign_hint") : isSale ? t("assign.sale_hint") : t("assign.sample_hint")}
            </p>
            {showOnlySampleOption && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="no-distributors-hint">
                {t("assign.no_distributors_sample_hint")}
              </p>
            )}
          </div>
          )}

          {/* Seller picker — Mark Sold ONLY. Includes "Me" + every distributor. */}
          {isSale && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.sold_by")}</label>
              <select
                className="input-brutal"
                value={sellerChoice}
                onChange={(e) => setSellerChoice(e.target.value)}
                disabled={lockDistributor && sellerChoice !== SELLER_ME}
                data-testid="assign-seller-select"
              >
                <option value={SELLER_ME}>{t("assign.seller_me")}</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id} data-testid={`seller-option-${d.id}`}>
                    {optionLabel(d)}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                {sellerChoice === SELLER_ME ? t("assign.seller_me_hint") : t("assign.seller_dist_hint")}
              </p>
            </div>
          )}

          {/* Distributor — Consignment ONLY (Sale uses the seller picker above) */}
          {isConsign && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.distributor")}</label>
              {hasDistributors ? (
                <select
                  className="input-brutal"
                  value={distributorId}
                  onChange={(e) => setDistributorId(e.target.value)}
                  disabled={lockDistributor}
                  data-testid="assign-distributor-select"
                >
                  {!lockDistributor && (
                    <option value="">— {t("assign.pick_recipient_placeholder")} —</option>
                  )}
                  {distributors.map((d) => (
                    <option key={d.id} value={d.id}>{optionLabel(d)}</option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                  {t("assign.no_distributors")}
                </div>
              )}
              {distributors.find((d) => d.id === distributorId)?.is_personal && (
                <p className="text-[11px] text-[#8a6a1c] mt-1" data-testid="personal-pricing-hint">
                  {t("assign.personal_pricing_hint")}
                </p>
              )}
              {sheetInfo && (
                <p className="text-[11px] font-semibold text-[#8a6a1c] mt-1" data-testid="sheet-pricing-hint">
                  {t("assign.sheet_pricing_hint", {
                    currency: sheetInfo.currency,
                    ws: sheetInfo.ws != null ? `$${sheetInfo.ws}` : "—",
                    retail: sheetInfo.retail != null ? `$${sheetInfo.retail}` : "—",
                  })}
                </p>
              )}
            </div>
          )}

          {/* Quick markup — held-stock hops only: giver sets what the recipient sees */}
          {isConsign && heldSource && hasDistributors && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="quick-markup-block">
              <label className="block text-xs font-semibold mb-1 text-emerald-900">{t("assign.quick_markup_label")}</label>
              <BrutalInput
                type="number"
                min="0"
                step="0.01"
                value={quickMarkup}
                onChange={(e) => setQuickMarkup(e.target.value)}
                placeholder="0.00"
                data-testid="quick-markup-input"
              />
              <p className="text-[11px] text-emerald-800 mt-1" data-testid="quick-markup-preview">
                {Number(quickMarkup) > 0
                  ? t("assign.quick_markup_preview", {
                      price: (Number(sourceRow?.wholesale_price || 0) + Number(quickMarkup)).toFixed(2),
                    })
                  : t("assign.quick_markup_hint")}
              </p>
            </div>
          )}

          {/* Chained pass-down — same quantity flows through the whole chain.
              v402.9: explicit opt-in toggle + route preview so nothing routes
              "via Harvey" by accident. */}
          {isConsign && !holderUserId && distributorId && distributors.filter((d) => d.id !== distributorId).length > 0 && (
            !showChain ? (
              <button
                type="button"
                className="text-xs font-medium text-[#8a6a1c] underline underline-offset-2"
                onClick={() => setShowChain(true)}
                data-testid="chain-toggle-btn"
              >
                ↳ {t("assign.chain_toggle")}
              </button>
            ) : (
            <div className="rounded-lg border border-[#c9a24a] bg-[#fbf6e7]/60 p-3 space-y-2" data-testid="pass-now-block">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-[#8a6a1c]">{t("assign.pass_now_label")}</label>
                <button
                  type="button"
                  className="text-[11px] text-zinc-500 underline underline-offset-2"
                  onClick={() => { setShowChain(false); setPassTargetId(""); }}
                  data-testid="chain-cancel-btn"
                >
                  {t("common.cancel")}
                </button>
              </div>
              <select
                className="input-brutal w-full"
                value={passTargetId}
                onChange={(e) => setPassTargetId(e.target.value)}
                data-testid="pass-now-select"
              >
                <option value="">— {t("assign.pass_now_none")} —</option>
                {distributors
                  .filter((d) => d.id !== distributorId)
                  // v409.2 — chained hop sources from the intermediary: only
                  // THEIR personal entry is a valid final stop.
                  .filter((d) => !d.is_personal || d.owner_id === distributorId)
                  .map((d) => (
                  <option key={d.id} value={d.id}>{optionLabel(d)}</option>
                ))}
              </select>
              {passTargetId && (
                <p className="text-xs font-semibold text-[#8a6a1c]" data-testid="chain-route-preview">
                  {t("assign.chain_route", {
                    mid: distributors.find((d) => d.id === distributorId)?.name || "?",
                    name: distributors.find((d) => d.id === passTargetId)?.name || "?",
                  })}
                </p>
              )}
              {passTargetId && (
                <div>
                  <label className="block text-xs font-semibold mb-1 text-[#8a6a1c]">{t("assign.quick_markup_label")}</label>
                  <BrutalInput
                    type="number"
                    min="0"
                    step="0.01"
                    value={passMarkup}
                    onChange={(e) => setPassMarkup(e.target.value)}
                    placeholder="0.00"
                    data-testid="pass-now-markup-input"
                  />
                  <p className="text-[11px] text-[#8a6a1c] mt-1">
                    {Number(passMarkup) > 0
                      ? t("assign.quick_markup_preview", {
                          price: (Number(product.wholesale_price || 0) + Number(passMarkup)).toFixed(2),
                        })
                      : t("assign.quick_markup_hint")}
                  </p>
                </div>
              )}
              <p className="text-[11px] text-[#8a6a1c]">{t("assign.pass_now_hint")}</p>
            </div>
            )
          )}

          {/* Recipient name (Sample required, Sale optional) */}
          {isSample && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.recipient_name")}</label>
              <BrutalInput
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={t("assign.recipient_placeholder")}
                maxLength={200}
                data-testid="assign-recipient-input"
                autoFocus
              />
            </div>
          )}

          {/* Payment status for samples: free · paid up-front · owed (pay later) */}
          {isSample && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
              <label className="block text-xs font-semibold text-amber-900">{t("assign.sample_payment_label")}</label>
              <div className="grid grid-cols-3 gap-2" data-testid="sample-payment-toggle">
                <button
                  type="button"
                  onClick={() => setSamplePayment("free")}
                  data-testid="sample-payment-free"
                  className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                    samplePayment === "free"
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                >
                  {t("assign.sample_free_pill")}
                </button>
                <button
                  type="button"
                  onClick={() => setSamplePayment("paid")}
                  data-testid="sample-payment-paid"
                  className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                    samplePaid
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                >
                  {t("assign.sample_paid_pill")}
                </button>
                <button
                  type="button"
                  onClick={() => setSamplePayment("owed")}
                  data-testid="sample-payment-owed"
                  className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                    sampleOwed
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                  }`}
                >
                  {t("assign.sample_owed_pill")}
                </button>
              </div>
              <p className="text-[11px] text-amber-800" data-testid="sample-payment-hint">
                {samplePaid
                  ? t("assign.sample_paid_hint")
                  : sampleOwed
                    ? t("assign.sample_owed_hint")
                    : t("assign.sample_free_hint")}
              </p>
              {(samplePaid || sampleOwed) && (
                <div>
                  <label className="block text-xs font-semibold mb-1 text-amber-900">
                    {t("assign.sale_price_label", { unit: product.unit || t("common.units") })}
                  </label>
                  <BrutalInput
                    type="number"
                    min="0"
                    step="0.01"
                    value={samplePrice}
                    onChange={(e) => setSamplePrice(e.target.value)}
                    placeholder={product.wholesale_price != null ? String(product.wholesale_price) : "0.00"}
                    data-testid="sample-paid-price-input"
                  />
                  <PriceChips
                    wholesale={product.wholesale_price}
                    retail={product.suggested_retail_price}
                    value={samplePrice}
                    onPick={(v) => setSamplePrice(v)}
                    testIdPrefix="sample-paid-price-chip"
                  />
                  <p className="text-[11px] text-amber-800 mt-1">
                    {product.wholesale_price != null
                      ? t("assign.sample_paid_default_wholesale", { wholesale: product.wholesale_price })
                      : t("assign.sample_paid_default_zero")}
                  </p>
                  {sampleOwed && Number(samplePrice) > 0 && Number(quantity) > 0 && (
                    <p className="text-xs font-semibold text-amber-900 mt-1" data-testid="sample-owed-subtotal">
                      {t("assign.sample_owed_subtotal", {
                        total: (Number(samplePrice) * Math.max(1, Number(quantity) || 1)).toFixed(2),
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {isSale && sellerChoice === SELLER_ME && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.recipient_optional")}</label>
              <BrutalInput
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={t("assign.recipient_optional_placeholder")}
                maxLength={200}
                data-testid="assign-recipient-optional-input"
              />
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-xs font-semibold mb-1">{t("assign.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={sourceStock}
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                if (isSale && mist && !priceDirty) {
                  const p = tierUnitPrice(tiers, e.target.value);
                  if (p != null) setSalePrice(String(p));
                }
              }}
              data-testid="assign-quantity-input"
            />
            {useOwnHeld && (
              <p className="text-[11px] text-[#8a6a1c] mt-1" data-testid="own-held-hint">
                {t("assign.own_stock_hint", { n: sourceStock })}
              </p>
            )}
          </div>

          {isSale && mist && <TierBreakdown tiers={tiers} qty={quantity} />}

          {/* Sale price (Sale only) */}
          {isSale && (
            <div>
              <label className="block text-xs font-semibold mb-1">
                {t("assign.sale_price_label", { unit: product.unit || t("common.units") })}
              </label>
              <BrutalInput
                type="number"
                min="0"
                step="0.01"
                value={salePrice}
                onChange={(e) => {
                  setPriceDirty(true);
                  setSalePrice(e.target.value);
                }}
                placeholder={product.suggested_retail_price != null ? String(product.suggested_retail_price) : "0.00"}
                data-testid="assign-sale-price-input"
              />
              <PriceChips
                wholesale={sourceRow?.wholesale_price ?? product.wholesale_price}
                retail={product.suggested_retail_price}
                value={salePrice}
                onPick={(v) => { setPriceDirty(true); setSalePrice(v); }}
                testIdPrefix="assign-sale-price-chip"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                {product.suggested_retail_price != null
                  ? t("assign.sale_price_hint_srp", { srp: product.suggested_retail_price })
                  : t("assign.sale_price_hint_no_srp")}
              </p>
              <div className="mt-2">
                <SaleSummary
                  qty={quantity}
                  unitPrice={salePrice}
                  wholesale={sourceRow?.wholesale_price ?? product.wholesale_price}
                />
              </div>
            </div>
          )}

          {/* Note — Sale + Sample (anything quick-trackable) */}
          {!isConsign && (
            <div>
              <label className="block text-xs font-semibold mb-1">{t("assign.note_label")}</label>
              <textarea
                className="input-brutal h-20 resize-none"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  isSample
                    ? t("assign.note_placeholder_sample")
                    : sellerChoice === SELLER_ME
                      ? t("assign.note_placeholder_self")
                      : t("assign.note_placeholder_dist")
                }
                maxLength={500}
                data-testid="assign-note-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">{t("assign.note_hint")}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={busy || (isConsign && !hasDistributors)}
              data-testid="assign-submit"
            >
              {busy ? t("assign.sending") : isSample ? t("assign.log_sample") : isSale ? t("assign.log_sale") : t("assign.give")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}
